import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import { ContextHelp } from "../../UIElements/ContextHelp";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardArrow,
} from "../../UIElements/HoverCard";

export const StatCard = ({
  title,
  titleHelp = null,
  value,
  change,
  icon,
  yesterday,
  gradient = false,
  valueColor = "text-white",
  iconColor = "text-blue-400",
  iconBg = "bg-blue-500/10",
  className = "",
  changeColor = null,
}) => {
  const { t } = useTranslation("common");
  const ChangeElement = () => {
    const changeElement = change ? (
      <p
        className={`text-sm ${
          changeColor ??
          (parseFloat(change) === 0
            ? "text-yellow-500"
            : change.startsWith("+")
              ? "text-green-500"
              : "text-red-500")
        }`}
      >
        {change}
      </p>
    ) : (
      <div className="h-5 text-sm" aria-hidden="true"></div>
    );

    if (change && yesterday !== undefined && yesterday !== null) {
      return (
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div className="cursor-pointer">{changeElement}</div>
          </HoverCardTrigger>
          <HoverCardContent
            side="bottom"
            align="center"
            className="border-brand-cyan/20 w-40 border bg-gradient-to-br from-gray-800 to-gray-900 shadow-xl"
          >
            <HoverCardArrow className="fill-gray-800" />
            <div className="">
              <div className="flex items-center space-x-2">
                <div className="bg-brand-cyan/10 rounded-md">
                  <Icon name="Calendar" className="text-brand-cyan h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-300">
                    {t("date.previousPeriod")}
                  </p>
                  <p className={`text-lg font-bold ${valueColor}`}>
                    {yesterday}
                  </p>
                </div>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    }

    return changeElement;
  };

  return (
    <div
      className={`${
        gradient
          ? "bg-brand-gradient-dark border-brand-cyan/20"
          : "border-gray-800 bg-gray-900"
      } rounded-lg border p-4 shadow-lg ${className}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="mb-1 flex items-center gap-1 text-sm text-gray-300">
            <span>{title}</span>
            {titleHelp ? <ContextHelp {...titleHelp} /> : null}
          </p>
          <div className={`text-2xl font-semibold ${valueColor} mb-1`}>
            {value}
          </div>
          <ChangeElement />
        </div>
        {icon && (
          <div className={`p-3 ${iconBg} rounded-lg`}>
            <Icon name={icon} className={`h-6 w-6 ${iconColor}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export const StatsGrid = ({ stats, columns = 3, className = "" }) => {
  const getGridClass = () => {
    switch (columns) {
      case 1:
        return "grid grid-cols-1 gap-3 lg:gap-4";
      case 2:
        return "grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4";
      case 3:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4";
      case 4:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4";
      case 5:
        return "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4";
      case 6:
        return "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4";
      default:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4";
    }
  };

  return (
    <div className={`${getGridClass()} ${className}`}>
      {stats.map((stat, index) => (
        <StatCard key={stat.id || index} {...stat} />
      ))}
    </div>
  );
};
